// Copyright © 2022 Vewd Software AS.
//
// This file is part of Vewd Cloud,
// and includes Vewd Confidential Information.
// Distribution is strictly prohibited without Vewd's written consent.
import { useState, useRef, useEffect } from "react";

import Editor from "@monaco-editor/react";
import { useField } from "formik";
import debounce from "lodash-es/debounce";
import PropTypes from "prop-types";

import { FileButton, Loader } from "components/elements";
import { Switch } from "components/form/elements";
import { trans } from "src/translations";
import { classes } from "utils/classes";
import { getFieldTestId } from "utils/formik";

import { Label, ValidationBadge } from "..";
import { THEMES, useEditorTheme } from "../../elements/CodeEditor";
import styles from "./EditorField.scss";
import { createMixedValidator } from "./fieldUtils";
import { FieldProps } from "./propTypes";

const debounceDelay = 200;

export const EditorField = (props) => {
  const defaultTheme = useEditorTheme();
  const {
    name,
    label,
    type,
    look = "wrapped",
    height = "500px",
    className,
    accept,
    readOnly,
    required = true,
    tooltip,
    uploadActionText = trans.UPLOAD_JSON(),
    validate: validateFn,
    editorTheme = defaultTheme.editorTheme,
    toggleTheme = defaultTheme.toggleTheme,
    dataTestId,
  } = props;

  const [field, meta, helpers] = useField({
    ...props,
    type: "text",
    validate: createMixedValidator(name, required, validateFn),
  });
  const editorRef = useRef(null);
  const [editorReady, setEditorReady] = useState(false);
  const isLightTheme = editorTheme === THEMES.light;
  const testId = dataTestId || getFieldTestId(name);

  // Handles case of switching many EditorFields on tabs (when full remount does not happen)
  useEffect(() => {
    if (editorRef.current) {
      editorRef.current.getModel().setValue(field.value ?? "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name]);

  // This is for clearing field value after form reset
  useEffect(() => {
    if (editorRef.current && !meta.touched) {
      editorRef.current.getModel().setValue(field.value ?? "");
    }
  }, [meta.touched, field.value]);

  // Editor's defaultValue was not setting initial value, so that's the solution
  const handleOnMount = (editor) => {
    editorRef.current = editor;
    editor.getModel().setValue(field.value ?? "");
    setEditorReady(true);
  };

  const readFileContent = (e) => {
    const fileReader = new FileReader();
    fileReader.onload = () => {
      editorRef.current.getModel().setValue(fileReader.result);
      helpers.setValue(fileReader.result);
    };
    fileReader.readAsText(e.target.files[0]);
  };

  const renderButtons = (
    <div className={styles.buttons}>
      {!readOnly ? (
        <FileButton
          disabled={!editorReady}
          accept={accept}
          text={uploadActionText}
          className={styles.uploadButton}
          onChange={readFileContent}
        />
      ) : null}
      <div className={styles.themeSwitch}>
        <p className={styles.modeName} data-test-id={`${testId}-theme`}>
          {isLightTheme ? trans.LIGHT_MODE() : trans.DARK_MODE()}
        </p>
        <Switch
          className={styles.switchButton}
          checked={isLightTheme}
          onChange={toggleTheme}
          dataTestId={`${testId}-theme-switch`}
        />
      </div>
    </div>
  );

  return (
    <div className={classes(styles[look], className)} data-test-id={testId}>
      {label && (
        <Label
          text={label}
          tooltip={tooltip}
          required={required}
          dataTestId={`${testId}-label`}
        />
      )}
      {renderButtons}
      <div data-test-id={`${testId}-editor`}>
        <Editor
          className={styles.editor}
          height={height}
          language={type}
          loading={<Loader />}
          options={{
            automaticLayout: true,
            minimap: { enabled: false },
            readOnly,
          }}
          theme={editorTheme}
          onChange={debounce((value) => {
            if (!meta.touched) {
              helpers.setTouched(true);
            }
            helpers.setValue(value);
          }, debounceDelay)}
          onMount={handleOnMount}
        />
      </div>
      <ValidationBadge
        error={meta.error}
        touched={meta.touched}
        dataTestId={`${testId}-validation-badge`}
      />
    </div>
  );
};

EditorField.propTypes = {
  ...FieldProps,
  type: PropTypes.string.isRequired,
  height: PropTypes.string,
  accept: PropTypes.array.isRequired,
  uploadActionText: PropTypes.string.isRequired,
  editorTheme: PropTypes.oneOf([THEMES.light, THEMES.dark]),
  toggleTheme: PropTypes.func,
};
